import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/components/table";
import { Meeting, meetingService } from "@/api/calendar";
import { userRendezvousColumns } from "@/views/users/page/user_rendezvous_columns";
import DataTableSkeleton from "@/components/ui/data-table-skeleton";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

interface UserRendezvousTableProps {
  userId: string;
  initialMaximized?: boolean;
}

export function UserRendezvousTable({ userId, initialMaximized = false }: UserRendezvousTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMaximized, setIsMaximized] = useState(initialMaximized);
  const [rendezvous, setRendezvous] = useState<Meeting[]>([]);
  const [loadingRendezvous, setLoadingRendezvous] = useState(false);
  const [rendezvousPagesCnt, setRendezvousPagesCnt] = useState<number>(0);
  const [rendezvousParams, setRendezvousParams] = useState({
    page: 0,
    page_size: 5,
  });

  const rendezvousColumns = userRendezvousColumns();

  useEffect(() => {
    if (userId && isMaximized) {
      setLoadingRendezvous(true);
      meetingService.getMeetingsPaginated({
        start: -1,
        assigned_to: userId,
        page: rendezvousParams.page,
        page_size: rendezvousParams.page_size
      })
        .then((resp) => {
          setRendezvous(resp.results);
          setRendezvousPagesCnt(Math.ceil(resp.count / rendezvousParams.page_size));
        })
        .catch(() => {
          setRendezvous([]);
          setRendezvousPagesCnt(0);
        })
        .finally(() => {
          setLoadingRendezvous(false);
        });
    }
  }, [userId, rendezvousParams, isMaximized]);

  const handleRendezvousRowClick = (_row: Meeting) => {
    navigate({ to: '/dashboard' });
  };

  const toggleMaximized = () => setIsMaximized(!isMaximized);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-6 w-6" />
            <CardTitle>{t('comingRendezvous')}</CardTitle>
          </div>
          {!initialMaximized && (<Button variant="ghost" size="sm" onClick={toggleMaximized}>
            {isMaximized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <span className="sr-only">{isMaximized ? t('minimize') : t('maximize')}</span>
          </Button>)}
        </div>
        <CardDescription>{t('comingRendezvousDescription')}</CardDescription>
      </CardHeader>
      {isMaximized && (
        <CardContent>
          {loadingRendezvous ? (
            <DataTableSkeleton with_filter={false} columns={1} rows={rendezvousParams.page_size} />
          ) : rendezvous.length > 0 ? (
            <DataTable<Meeting>
              columns={rendezvousColumns}
              data={rendezvous}
              pages={rendezvousPagesCnt}
              params={rendezvousParams}
              setParams={setRendezvousParams}
              hideHeader={true}
              onRowClick={handleRendezvousRowClick}
            />
          ) : (
            <p>{t('noComingRendezvous')}</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
